import { ApprovalStep } from './expense.model';
import { PHIDataType } from './audit-log.model';

export enum TaskStatus {
  draft = 'draft',
  pendingApproval = 'pending_approval',
  approved = 'approved',
  assigned = 'assigned',
  inProgress = 'in_progress',
  blocked = 'blocked',
  completed = 'completed',
  cancelled = 'cancelled',
}

export const TaskStatusLabels: Record<TaskStatus, string> = {
  [TaskStatus.draft]: 'Draft',
  [TaskStatus.pendingApproval]: 'Pending Approval',
  [TaskStatus.approved]: 'Approved',
  [TaskStatus.assigned]: 'Assigned',
  [TaskStatus.inProgress]: 'In Progress',
  [TaskStatus.blocked]: 'Blocked',
  [TaskStatus.completed]: 'Completed',
  [TaskStatus.cancelled]: 'Cancelled',
};

export enum TaskPriority {
  low = 'low',
  medium = 'medium',
  high = 'high',
  urgent = 'urgent',
}

export const TaskPriorityLabels: Record<TaskPriority, string> = {
  [TaskPriority.low]: 'Low',
  [TaskPriority.medium]: 'Medium',
  [TaskPriority.high]: 'High',
  [TaskPriority.urgent]: 'Urgent',
};

export interface TaskSubtask {
  id: string;
  title: string;
  isCompleted: boolean;
  completedAt?: Date;
  completedBy?: string;
}

export interface Task {
  id: string;

  // Source
  meetingId: string;
  meetingTitle: string;
  sourceTimestampStart?: number;
  sourceTimestampEnd?: number;
  sourceTextSnippet?: string;

  // Task Content
  title: string;
  description: string;
  acceptanceCriteria?: string[]; // What defines "done"

  // Assignment (requires approval)
  assigneeId?: string; // User ID
  assigneeName?: string;
  assigneeEmail?: string;
  assignmentApprovalRequired: boolean;
  assignmentApprovedBy?: string;
  assignmentApprovedAt?: Date;

  // Timing (requires approval)
  dueDate?: Date;
  dueDateApprovalRequired: boolean;
  dueDateApprovedBy?: string;
  dueDateApprovedAt?: Date;
  estimatedHours?: number;

  // Classification
  priority: TaskPriority;
  category: string; // 'development', 'design', 'research', etc.

  // Status
  status: TaskStatus;
  approvalWorkflow: ApprovalStep[];

  // Progress Tracking
  startedAt?: Date;
  completedAt?: Date;
  blockedReason?: string;
  blockedAt?: Date;
  cancelledReason?: string;
  cancelledAt?: Date;

  // Subtasks
  subtasks?: TaskSubtask[];

  // Dependencies
  dependsOnTaskIds: string[];
  blockedByTaskIds: string[];
  relatedDecisionIds: string[];
  relatedRiskIds: string[];

  // PHI Detection
  containsPHI: boolean;
  phiDataTypes: PHIDataType[];

  // AI Confidence
  aiConfidence: number;
  aiExtractionNotes?: string;

  // Metadata
  tags: string[];
  projectId?: string;
  customFields?: Record<string, unknown>;

  // Audit
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
}

export interface TaskAnalytics {
  totalTasks: number;
  byStatus: Record<TaskStatus, number>;
  byPriority: Record<TaskPriority, number>;
  completionRate: number; // percentage
  avgCompletionTime: number; // days
  overdueCount: number;
  blockedCount: number;
}
